<template>
  <DropdownButton
    contentClass="button-dropdown-content"
    v-model:isOpen="isDropdownOpen"
    :contentClickCloses="items.type !== 'range'"
    isAdjustedContent
  >
    <Button class="p-button-sm p-button-secondary p-button-rounded">
      <span
        class="name-label"
        v-text="
          labelWrapper(items, activeFilters, citiesAndProvinces, districts)
        "
      />

      <i
        v-if="displayCloseIcon"
        @click.stop="clearDropdownFilter"
        class="pi pi-times ml-2 c-primary"
      />
      <i class="pi pi-chevron-down ml-2 c-primary"></i>
    </Button>
    <template #content>
      <DashboardDropdownList
        v-if="items.filterName === 'propertyType'"
        @dropdown-click="handleDropdownEvent($event, items.value)"
        :items="items"
      />
      <NumberRange
        v-if="items.filterName === 'price'"
        unit="currency"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
      <DashboardDropdownList
        v-if="items.filterName === 'city'"
        @dropdown-click="handleDropdownEvent($event, items.value)"
        :items="items"
        @bottomReach="fetchCitiesAndProvinces"
        :additional-items="citiesAndProvinces"
      />
      <DashboardDropdownList
        v-if="items.filterName === 'district'"
        @dropdown-click="handleDropdownEvent($event, items.value)"
        :items="items"
        :additional-items="districts"
      />
      <NumberRange
        v-if="items.filterName === 'rooms'"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
      <NumberRange
        v-if="items.filterName === 'area'"
        unit="area"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
      <NumberRange
        v-if="items.filterName === 'plotArea'"
        unit="area"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
      <NumberRange
        v-if="items.filterName === 'floor'"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
      <NumberRange
        v-if="items.filterName === 'constructionYear'"
        :items="items.value || []"
        :filtersObject="filtersState"
        @filter-data="$emit('add-to-queue', $event)"
        @number-range-enter="$emit('apply-filters')"
      />
    </template>
  </DropdownButton>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue'
import DashboardDropdownList from './DashboardDropdownList.vue'
import { useAppStore } from '@bd/client/store'
import { labelWrapper } from '@bd/components'
import { FilterData } from '@bd/store-modules/types'
import {
  useComputedActiveFilters,
  DropdownButton,
  NumberRange,
  CITIES_PAGE_SIZE,
} from '@bd/components'
import { rangeValues } from '@bd/client/services/mutation.service'

export default defineComponent({
  name: 'DashboardDropdown',
  components: {
    DropdownButton,
    NumberRange,
    DashboardDropdownList,
  },
  emits: ['dropdown-click', 'apply-filters', 'add-to-queue'],
  props: {
    label: {
      type: String,
      default: 'Label',
    },
    items: {
      type: Object as PropType<FilterData>,
      required: true,
    },
  },
  setup(props) {
    const store = useAppStore()
    const activeFilters = computed(() =>
      useComputedActiveFilters(store.state.offers?.searchedFilters || {}),
    )
    const isDropdownOpen = ref(false)
    const districts = computed(() => store.state.offers?.districts)

    const handleDropdownEvent = async (
      option: string | number,
      value: string[],
    ) => {
      const list = ['cityIds', 'districtIds']
      if (list.includes(value[0])) {
        await store.dispatch('offers/setFilterAndRefresh', {
          [value[0]]: option,
          address: undefined,
        })
      } else {
        await store.dispatch('offers/setFilterAndRefresh', {
          [value[0]]: option,
        })
      }
    }

    const citiesAndProvinces = computed(() => {
      return store.state.offers?.citiesAndProvinces.content?.content || []
    })

    const filtersState = computed(() => {
      return store.state.offers?.filters
    })

    const fetchCitiesAndProvinces = () => {
      store.dispatch('offers/citiesAndProvincesList', CITIES_PAGE_SIZE)
    }

    const filterName = computed(() => props.items.filterName)

    const activeFilter = computed(() =>
      activeFilters.value.find((x) => x.filterName === filterName.value),
    )

    const displayCloseIcon = computed(() => {
      const filterHasValue =
        activeFilter.value?.children[0].value.length ||
        typeof activeFilter.value?.children[0].value === 'number'

      return activeFilter.value && filterHasValue
    })

    const clearDropdownFilter = () => {
      const currentFilter = activeFilter.value?.children

      if (!currentFilter?.[0]) {
        return
      }

      currentFilter.forEach((filter) => {
        const { name, value } = filter
        const payload = rangeValues.includes(name) ? 0 : value
        store.commit('offers/SET_FILTERS', { [name]: payload })
      })

      store.commit('offers/APPLY_FILTERS')
    }

    return {
      ...props,
      labelWrapper,
      districts,
      activeFilters,
      handleDropdownEvent,
      isDropdownOpen,
      citiesAndProvinces,
      filtersState,
      fetchCitiesAndProvinces,
      displayCloseIcon,
      clearDropdownFilter,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.button-dropdown-content.dropdown-content) {
  top: 50px;
  padding: 25px;
  border-radius: 7px;
  box-shadow: 0 25px 190px 0 $light-shadow;
  background: #ffffff;
  z-index: 2;
  width: 200px;
  @include breakpoint-up(md) {
    min-width: 400px;
  }
}
</style>
